import React from 'react';
import { FaSmile, FaMeh, FaFrown } from 'react-icons/fa';

export const SentimentIcon = ({ score, size = 'md' }) => {
  if (score === null || score === undefined || isNaN(score)) {
    return (
      <div className={`flex items-center justify-center ${getSizeClasses(size)}`}>
        <span className="text-gray-400">-</span>
      </div>
    );
  }

  const numScore = Number(score);
  const { icon, color, bgColor } = getSentimentConfig(numScore);
  const sizeClasses = getSizeClasses(size);

  return (
    <div className={`flex items-center justify-center ${sizeClasses}`} title={`Sentiment Score: ${numScore}`}>
      <div className={`rounded-full ${bgColor} flex items-center justify-center ${sizeClasses}`}>
        <span className={`${color} text-lg`}>{icon}</span>
      </div>
    </div>
  );
};

const normalizeScore = (score) => {
  const num = Number(score);
  if (num <= 5 && num >= -5) {
    return ((num + 5) / 10) * 100;
  }
  if (num <= 1 && num >= -1) {
    return (num + 1) * 50;
  }
  return num;
};

const getSentimentConfig = (rawScore) => {
  const score = normalizeScore(rawScore);
  if (score >= 70) {
    return {
      icon: <FaSmile />,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    };
  } else if (score >= 40) {
    return {
      icon: <FaMeh />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    };
  } else {
    return {
      icon: <FaFrown />,
      color: 'text-red-600',
      bgColor: 'bg-red-100'
    };
  }
};

const getSizeClasses = (size) => {
  switch (size) {
    case 'sm':
      return 'w-6 h-6 text-sm';
    case 'lg':
      return 'w-10 h-10 text-xl';
    case 'xl':
      return 'w-12 h-12 text-2xl';
    default:
      return 'w-8 h-8 text-base';
  }
};

// Alternative text-based sentiment indicator
export const SentimentText = ({ score, showScore = true }) => {
  if (score === null || score === undefined || isNaN(score)) {
    return <span className="text-gray-500">Unknown</span>;
  }

  const numScore = Number(score);
  const { text, color } = getSentimentTextConfig(numScore);

  return (
    <span className={color}>
      {text} {showScore && `(${numScore})`}
    </span>
  );
};

const getSentimentTextConfig = (rawScore) => {
  const score = normalizeScore(rawScore);
  if (score >= 70) {
    return {
      text: 'Positive',
      color: 'text-green-600'
    };
  } else if (score >= 40) {
    return {
      text: 'Neutral',
      color: 'text-yellow-600'
    };
  } else {
    return {
      text: 'Negative',
      color: 'text-red-600'
    };
  }
};

// Sentiment progress bar
export const SentimentBar = ({ score, height = 'h-2' }) => {
  if (score === null || score === undefined || isNaN(score)) {
    return (
      <div className={`w-full bg-gray-200 rounded-full ${height}`}>
        <div className="bg-gray-400 h-full rounded-full w-0"></div>
      </div>
    );
  }

  const numScore = Number(score);
  const width = Math.max(0, Math.min(100, normalizeScore(numScore)));
  const { color } = getSentimentConfig(numScore);

  return (
    <div className={`w-full bg-gray-200 rounded-full ${height}`} title={`Sentiment Score: ${numScore}%`}>
      <div 
        className={`${color.replace('text-', 'bg-')} h-full rounded-full transition-all duration-300`}
        style={{ width: `${width}%` }}
      ></div>
    </div>
  );
};

export default SentimentIcon;